import { useTranslation } from 'module/hooks/use-translation';
import { Alert } from 'module/ui/components/atoms/alert';
import { Button } from 'module/ui/components/atoms/button';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import {
  ExpandableSearchInput,
  ExpandableSearchInputRef,
} from 'module/ui/components/atoms/input/search-input';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { HeaderWithIconTitle } from 'module/ui/components/molecules/page/page';
import { Argon } from 'module/ui/components/templates/argon';
import {
  faPlus,
  // faChevronDown,
  faFileLines,
} from '@fortawesome/pro-regular-svg-icons';
// import { faBook, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { PageTemplateLite, SourceType } from 'api/page-template/types';
import {
  useDeleteMeetingPageTemplateMutation,
  useGetAllMeetingPageTemplatesQuery,
  useUpdateMeetingPageTemplateOrderMutation,
  useUpdateMeetingPageTemplateStatusMutation,
} from 'queries/meeting-page-template';
import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from 'routes/route-paths';
// import styled from 'styled-components';
import { FlexWrapperWithShortGap } from 'components/common/styles';
import {
  PageTemplatesNoSearchMatchState,
  PageTemplatesNoTemplatesState,
} from 'components/organisms/document/shared/empty-state';
import { TemplateTable } from 'components/organisms/pages-template/shared/page-template-table';
import { ContentWrapper, ListLoader } from '../styles';

export type MeetingTemplatePermissions = {
  canRead: boolean;
  canCreate: boolean;
  canWrite: boolean;
  canDelete: boolean;
};

export const MeetingPageTemplates = ({
  meetingTemplatePermissions,
  isModuleFed = true,
}: {
  meetingTemplatePermissions?: MeetingTemplatePermissions;
  isModuleFed?: boolean;
} = {}) => {
  const title = <FormattedMessage id="meeting_page_templates.title" />;
  const navigate = useNavigate();
  const perms = isModuleFed ? meetingTemplatePermissions : undefined;
  const canCreate = perms?.canCreate !== false;
  const canRead = perms?.canRead !== false;
  const canWrite = perms?.canWrite !== false;
  const canDelete = perms?.canDelete !== false;
  const templateTablePermissions =
    perms != null ? { canRead, canWrite, canCreate, canDelete } : undefined;
  const { value: searchQuery, onChange: onSearchChange } =
    useSearchValueState();
  const searchInputRef = useRef<ExpandableSearchInputRef>(null);
  const { data: apiTemplates, isLoading } =
    useGetAllMeetingPageTemplatesQuery();
  const [templates, setTemplates] = React.useState<PageTemplateLite[]>([]);
  const deleteMeetingPageTemplateMutation =
    useDeleteMeetingPageTemplateMutation();

  const updateMeetingPageTemplateOrderMutation =
    useUpdateMeetingPageTemplateOrderMutation();

  const updateMeetingPageTemplateStatusMutation =
    useUpdateMeetingPageTemplateStatusMutation();

  React.useEffect(() => {
    if (apiTemplates) {
      setTemplates(apiTemplates);
    }
  }, [apiTemplates]);

  const filteredTemplates = React.useMemo(() => {
    if (!searchQuery) {
      return templates;
    }

    return templates.filter((template) =>
      template.templateName.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [templates, searchQuery]);

  const handleDelete = async (id: string) => {
    try {
      await deleteMeetingPageTemplateMutation.mutateAsync(Number(id));
    } catch (error) {
      // toast.error(error.message);
      // eslint-disable-next-line no-console
      console.error(error);
    }
    // setTemplates(
    //   templates.filter((template) => String(template.templateId) !== id),
    // );
  };

  const handleStatusChange = async (id: string) => {
    setTemplates(
      templates.map((template) =>
        String(template.templateId) === id
          ? { ...template, enabled: !template.enabled }
          : template,
      ),
    );
    await updateMeetingPageTemplateStatusMutation.mutateAsync({
      templateId: Number(id),
      status: !templates.find((template) => String(template.templateId) === id)
        ?.enabled,
    });
  };

  const handleReorder = async (
    newTemplates: PageTemplateLite[],
    movedTemplateId: number,
    newIndex: number,
  ) => {
    try {
      setTemplates(newTemplates);
      await updateMeetingPageTemplateOrderMutation.mutateAsync({
        templateId: movedTemplateId,
        order: newIndex,
      });
    } catch (error) {
      // toast.error(error.message);
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  return (
    <Argon
      documentTitle={useTranslation('meeting_page_templates.title')}
      title={
        <HeaderWithIconTitle
          icon={<FontAwesomeIcon icon={faFileLines} size={20} />}
          title={title}
        />
      }
      description={<FormattedMessage id="meeting_page_templates.description" />}
      actions={{
        secondary: (
          <FlexWrapperWithShortGap>
            <ExpandableSearchInput
              ref={searchInputRef}
              direction="right"
              value={searchQuery}
              onChange={onSearchChange}
              searchButtonProps={{
                type: 'text',
              }}
              minWidth="360px"
            />
            {/* <Button>
              <FlexWrapperWithShortGap>
                <StyledBookIcon icon={faBook} />
                <FontAwesomeIcon icon={faChevronDown} />
              </FlexWrapperWithShortGap>
            </Button> */}

            {canWrite && (
              <Button
                icon={<FontAwesomeIcon icon={faPlus} size={12} />}
                type="primary"
                onClick={() => navigate(ROUTE_PATHS.NEW)}
              >
                <FormattedMessage id="new_template" />
              </Button>
            )}
          </FlexWrapperWithShortGap>
        ),
      }}
      content={
        isLoading ? (
          <ListLoader />
        ) : (
          <ContentWrapper>
            {(apiTemplates?.length ?? 0) === 0 ? (
              <PageTemplatesNoTemplatesState
                sourceType={SourceType.Meeting}
                onPrimaryAction={
                  canWrite ? () => navigate(ROUTE_PATHS.NEW) : () => {}
                }
              />
            ) : filteredTemplates.length === 0 ? (
              <PageTemplatesNoSearchMatchState
                sourceType={SourceType.Meeting}
                onPrimaryAction={() =>
                  searchInputRef.current?.closeSearchInput()
                }
              />
            ) : (
              <>
                <Alert
                  type="info"
                  icon={faInfoCircle}
                  content={
                    <FormattedMessage id="meeting_page_templates.info.content" />
                  }
                />

                <TemplateTable
                  templates={filteredTemplates}
                  onStatusChange={handleStatusChange}
                  onEdit={(id: string) => {
                    navigate(`${id}`);
                  }}
                  onDelete={handleDelete}
                  templatePermissions={templateTablePermissions}
                  isModuleFed={isModuleFed}
                  onReorder={handleReorder}
                  loading={isLoading}
                />
              </>
            )}
          </ContentWrapper>
        )
      }
    />
  );
};
